import { providers, utils, Contract } from "ethers";
import {
  Provider,
  setMulticallAddress,
  Contract as MulContract,
} from "ethers-multicall";
import util from "./util";

const abi = [
  {
    inputs: [],
    name: "name",
    outputs: [
      {
        internalType: "string",
        name: "",
        type: "string",
      },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "owner",
        type: "address",
      },
    ],
    name: "balanceOf",
    outputs: [
      {
        internalType: "uint256",
        name: "",
        type: "uint256",
      },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      {
        internalType: "address",
        name: "owner",
        type: "address",
      },
      {
        internalType: "uint256",
        name: "index",
        type: "uint256",
      },
    ],
    name: "tokenOfOwnerByIndex",
    outputs: [
      {
        internalType: "uint256",
        name: "",
        type: "uint256",
      },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      {
        internalType: "uint256",
        name: "tokenId",
        type: "uint256",
      },
    ],
    name: "tokenURI",
    outputs: [
      {
        internalType: "string",
        name: "",
        type: "string",
      },
    ],
    stateMutability: "view",
    type: "function",
  },
];

class User {
  constructor(rpc) {
    this.api = new providers.JsonRpcProvider(rpc);
  }

  // 获取钱包账号
  async getAccounts(account) {
    return await this.setSigner(account).getAddress();
  }

  async setSigner(account) {
    return await this.api.getSigner(account);
  }

  // 获取账户余额
  async getBalance(account) {
    const balance = await this.api.getBalance(account);
    return utils.formatEther(balance);
  }

  // 获取gas
  async estimateGas({ from, to, value, data }) {
    return await this.api.estimateGas({
      from,
      to,
      value,
      data,
    });
  }

  // 获取gasPrice
  async getGasPrice() {
    return await this.api.getGasPrice();
  }

  // 获取token 余额
  async getTokenBalance({ from, to }) {
    let data =
      "0x70a08231000000000000000000000000" + from?.slice(2)?.toLowerCase();
    return await this.api.call({
      from,
      to,
      data,
    });
  }

  async getNonce(address, tag = "latest") {
    return await this.api.getTransactionCount(address, tag);
  }

  async sendTransaction(signedTransaction) {
    return await this.api.sendTransaction(signedTransaction);
  }

  async call(transaction) {
    return await this.api.call(transaction);
  }

  async getProvider() {
    try {
      let eth = await util.getEthereum();
      if (!eth) {
        return null;
      }
      let provider = new providers.Web3Provider(eth);
      return provider;
    } catch (err) {
      return null;
    }
  }

  async getIsContract(nftContract) {
    try {
      let code = await this.api?.getCode(nftContract, "latest");
      return code != "0x";
    } catch (err) {
      return 0;
    }
  }

  isAddress(address) {
    return utils.isAddress(address);
  }

  decToHex64(dec) {
    let hex = Number(dec)?.toString(16);
    while (hex.length < 64) {
      hex = "0" + hex;
    }
    return hex;
  }

  async getOwner(tokenContract, tokenID) {
    try {
      let owner = await this.api?.call({
        to: tokenContract,
        data: "0x6352211e" + this.decToHex64(tokenID),
      });
      if (owner && owner?.length > 40) {
        return "0x" + owner.slice(-40).toLowerCase();
      }
      return "";
    } catch (err) {
      return "";
    }
  }

  getContractABI(address) {
    const abi = require("./ERC6551.json");
    const contract = new Contract(address, abi, this.api);
    return contract;
  }

  setMultiAddress() {
    setMulticallAddress(9001, "0x6BD3bD18b10d2eEb9FFf616DDDa166CbBE4d03A1");
    setMulticallAddress(10, "0x142E2FEaC30d7fc3b61f9EE85FCCad8e560154cc");
    setMulticallAddress(1284, "0xA63dc61430B5b2c1C18D39d753b49C12C2AA9091");
    setMulticallAddress(5611, "0x88E72A062B59f01c7d64238D9d9F25638e5b34E2");
  }

  async getCollection(contractAddress, account) {
    try {
      const ethcallProvider = new Provider(this.api);
      this.setMultiAddress();
      await ethcallProvider.init();
      let contract = new MulContract(contractAddress, abi);
      let [name, balance] = await await ethcallProvider.all([
        contract.name(),
        contract.balanceOf(account),
      ]);
      let collection = {
        name,
        count: balance?.toNumber() || 0,
        contract_address: contractAddress,
        custom: 1,
        open: false,
      };
      if (balance.lte(0)) {
        return collection;
      }
      // 获取nft tokenID
      let calls = [];
      for (let i = 0; i < balance; i++) {
        calls.push(contract.tokenOfOwnerByIndex(account, i));
      }
      let ids = await ethcallProvider.all(calls);
      if (!ids?.length) {
        return collection;
      }
      let uriCalls = ids?.map((v) => contract.tokenURI(v));
      let uris = await ethcallProvider.all(uriCalls);

      let jsons = uris?.map(async (v) => {
        let res = await fetch(v).catch();
        let json = await res?.json();
        return json?.result?.data;
      });

      let result = await Promise.allSettled([...jsons]);

      let nfts = ids?.map((v, i) => {
        return {
          token_id: v?.toString(),
          name: result[i]?.value?.name,
          image_url: result[i]?.value?.image,
          price: 0,
        };
      });
      return {
        ...collection,
        list: nfts,
      };
    } catch (err) {
      return null;
    }
  }
}

export default User;
